/**
 * lab-fase.js — waar een labopdracht staat, als één fase van vier.
 *
 * Een opdracht heeft twee statussen: die in GeoVoice (concept / verzonden) en die bij het lab
 * (concept / bevestigd / gerapporteerd). Twee kolommen naast elkaar lieten de gebruiker zelf
 * uitrekenen bij wie de bal lag. De fase doet dat voor hem, in de volgorde waarin een opdracht
 * ze doorloopt:
 *
 *   1 Concept             nog in GeoVoice: jij maakt hem af
 *   2 Bevestigen in @MIS  verzonden, maar het lab heeft hem als concept: jij, in het labportaal
 *   3 Bij het lab         bevestigd of in bewerking: het lab analyseert
 *   4 Resultaten binnen   gerapporteerd
 *
 * Afgekeurd is geen eigen fase maar een uitzondering op stap 3: de opdracht ligt bij het lab,
 * alleen gaat het daar niet verder.
 */
import { wachtOpBevestiging, labStatusPresentatie } from '../labs.js';

export const FASES = {
  concept: { stap: 1, label: 'Concept', sub: 'Jij maakt af' },
  bevestigen: { stap: 2, label: 'Bevestigen in @MIS', sub: 'Jij, in het labportaal' },
  lab: { stap: 3, label: 'Bij het lab', sub: 'Lab analyseert' },
  resultaten: { stap: 4, label: 'Resultaten binnen', sub: 'Klaar om te bekijken' },
};

export const FASE_VOLGORDE = ['concept', 'bevestigen', 'lab', 'resultaten'];

/** Is de opdracht de deur uit? Een verzonden opdracht heeft een verzenddatum of die status. */
export function isVerzonden(order) {
  return !!order && (!!order.sentAt || ['sent', 'in_progress', 'reported'].includes(order.status));
}

/** De fase van een opdracht: een sleutel uit FASES. */
export function faseVan(order) {
  if (!isVerzonden(order)) return 'concept';
  if (order.resultsStatus === 'available' || order.status === 'reported') return 'resultaten';
  if (labStatusPresentatie(order.labStatusName)?.klasse === 'ok') return 'resultaten';
  if (wachtOpBevestiging(order)) return 'bevestigen';
  return 'lab';
}

/** Heeft het lab de opdracht afgekeurd of geannuleerd? Dan kleurt stap 3 rood. */
export function isAfgekeurd(order) {
  return isVerzonden(order) && labStatusPresentatie(order.labStatusName)?.klasse === 'bad';
}

const gevuld = (v) => !!String(v ?? '').trim();

/**
 * Hoe ver een concept is: de drie stappen van het aanmaken, in de volgorde waarin je ze doet.
 *
 * Dezelfde regels als de checklist op de opdrachtpagina (lab-order-page `_checklist`), maar
 * met wat de lijst van elke opdracht meekrijgt: de administratieve velden, en per opdracht
 * hoeveel analysemonsters potjes en pakketten hebben.
 *
 * @returns {{klaar: boolean[], aantal: number, volgende: null | {stap: string, label: string}}}
 */
export function conceptVoortgang(order) {
  const o = order || {};
  const n = o.analysisSampleCount || 0;
  const admin = gevuld(o.projectLeaderName) && gevuld(o.projectLeaderEmail)
    && gevuld(o.urgencyCode) && gevuld(o.agreementCode) && gevuld(o.sampleDeliveryDate);
  const mengmonsters = n > 0 && (o.samplesWithParts ?? 0) >= n;
  const analyses = n > 0 && (o.samplesWithPackages ?? 0) >= n;
  const klaar = [admin, mengmonsters, analyses];
  const STAPPEN = [
    { stap: 'admin', label: 'Administratie invullen', kort: 'administratie' },
    { stap: 'composition', label: 'Mengmonsters samenstellen', kort: 'mengmonsters' },
    { stap: 'packages', label: 'Analyses kiezen', kort: 'analyses' },
  ];
  const open = klaar.indexOf(false);
  return {
    klaar,
    aantal: klaar.filter(Boolean).length,
    volgende: open === -1
      ? { stap: 'admin', label: 'Controleren en versturen', kort: 'versturen' }
      : STAPPEN[open],
  };
}

/** Per fase hoeveel opdrachten er staan. */
export function telFases(orders) {
  const tel = { concept: 0, bevestigen: 0, lab: 0, resultaten: 0 };
  for (const o of orders || []) tel[faseVan(o)] += 1;
  return tel;
}

/**
 * Per fase het moment waarop de opdracht erin kwam, voor de tooltip op de streepjes.
 *
 * Aanmaken en versturen gebeuren in GeoVoice en hebben een exact tijdstip. Bevestigen en
 * rapporteren gebeuren bij het lab; daarvan weten we alleen wanneer GeoVoice het zag, bij een
 * ronde langs het lab (labConfirmedAt / labReportedAt). Voor opdrachten van vóór 29-10-2026 is
 * dat niet bijgehouden: dan staat er 'tijdstip niet bekend' in plaats van een verzonnen datum.
 *
 * @returns {{fase: string, label: string, gebeurtenis: string, moment: string|null, bereikt: boolean}[]}
 */
export function faseMomenten(order) {
  const o = order || {};
  const huidig = FASES[faseVan(o)].stap;
  return [
    ['concept', 'Aangemaakt', o.createdAt],
    ['bevestigen', 'Verstuurd', o.sentAt],
    ['lab', 'Bevestigd door het lab', o.labConfirmedAt],
    ['resultaten', 'Gerapporteerd', o.labReportedAt],
  ].map(([fase, gebeurtenis, moment]) => ({
    fase,
    label: FASES[fase].label,
    gebeurtenis,
    moment: moment || null,
    bereikt: FASES[fase].stap <= huidig,
  }));
}
